import { Plus, X } from 'lucide-react';
import type { AttachmentSize } from '../../api/types';
import { Button, Field, FieldMessages } from '../../components/ui';
import { strings } from '../../strings';
import { SIZE_ORDER, sizeOptionLabel } from '../shared';
import type { AddressPlanForm } from './planForm';

const p = strings.networks.lists.plan;
const BASE = '/settings/addressPlan';

/**
 * The address plan's fields (spec 11 6.15.3 step 3, 6.15.8 Edit): supernets with Add and Remove, reserved ranges
 * (CIDR and label), the region and hub prefix lengths and the default attachment size. `errorsAt` gives the messages
 * at a JSON Pointer into the request body; `removeReason` disables a supernet's Remove (it holds allocations).
 */
export function AddressPlanFields({
  idPrefix,
  value,
  onChange,
  errorsAt,
  removeReason = () => null,
  supernetNote = null,
}: {
  idPrefix: string;
  value: AddressPlanForm;
  onChange: (value: AddressPlanForm) => void;
  errorsAt: (pointer: string) => string[];
  removeReason?: (cidr: string) => string | null;
  supernetNote?: string | null;
}) {
  const id = (suffix: string) => `${idPrefix}-plan-${suffix}`;
  const set = (patch: Partial<AddressPlanForm>) => onChange({ ...value, ...patch });
  return (
    <div className="net-plan-fields">
      <fieldset className="mb-3">
        <legend className="label">{p.supernets}</legend>
        <p className="help mb-1" id={id('supernets-help')}>
          {p.supernetsHelp}
        </p>
        <ul className="space-y-2">
          {value.supernets.map((cidr, i) => {
            const errors = errorsAt(`${BASE}/supernets/${i}`);
            const reason = removeReason(cidr);
            const reasonId = id(`supernet-${i}-reason`);
            return (
              <li key={i}>
                <div className="flex items-center gap-2">
                  <input
                    id={id(`supernet-${i}`)}
                    className="input max-w-[16rem] font-mono"
                    aria-label={p.supernet(i)}
                    value={cidr}
                    spellCheck={false}
                    autoComplete="off"
                    aria-invalid={errors.length ? true : undefined}
                    aria-describedby={errors.length ? id(`supernet-${i}-error`) : undefined}
                    onChange={(e) => {
                      const supernets = [...value.supernets];
                      supernets[i] = e.target.value;
                      set({ supernets, supernetsTouched: true });
                    }}
                  />
                  <Button
                    variant="ghost"
                    aria-label={p.removeSupernet(cidr)}
                    title={reason ?? p.removeSupernet(cidr)}
                    aria-disabled={reason || value.supernets.length <= 1 ? true : undefined}
                    aria-describedby={reason ? reasonId : undefined}
                    onClick={() => {
                      if (reason || value.supernets.length <= 1) return;
                      set({ supernets: value.supernets.filter((_, j) => j !== i), supernetsTouched: true });
                    }}
                  >
                    <X aria-hidden className="size-4" />
                  </Button>
                  {reason && (
                    <span id={reasonId} className="help">
                      {reason}
                    </span>
                  )}
                </div>
                <FieldMessages id={id(`supernet-${i}-error`)} messages={errors} />
              </li>
            );
          })}
        </ul>
        {supernetNote && <p className="help mt-1">{supernetNote}</p>}
        <FieldMessages messages={errorsAt(`${BASE}/supernets`).filter(() => value.supernets.length === 0)} />
        <Button
          className="mt-2"
          icon={<Plus aria-hidden className="size-4" />}
          onClick={() => set({ supernets: [...value.supernets, ''], supernetsTouched: true })}
        >
          {p.addSupernet}
        </Button>
      </fieldset>

      <fieldset className="mb-3">
        <legend className="label">{p.reserved}</legend>
        <p className="help mb-1">{p.reservedHelp}</p>
        {value.reserved.length === 0 && <p className="help">{p.noReserved}</p>}
        <ul className="space-y-2">
          {value.reserved.map((range, i) => {
            const errors = errorsAt(`${BASE}/reserved/${i}`);
            return (
              <li key={i}>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    className="input max-w-[12rem] font-mono"
                    aria-label={p.reservedCidr(i)}
                    value={range.cidr}
                    spellCheck={false}
                    autoComplete="off"
                    aria-invalid={errors.length ? true : undefined}
                    aria-describedby={errors.length ? id(`reserved-${i}-error`) : undefined}
                    onChange={(e) => {
                      const reserved = value.reserved.map((r, j) =>
                        j === i ? { ...r, cidr: e.target.value } : r,
                      );
                      set({ reserved });
                    }}
                  />
                  <input
                    className="input min-w-0 flex-1"
                    aria-label={p.reservedLabel(i)}
                    value={range.label}
                    maxLength={80}
                    onChange={(e) => {
                      const reserved = value.reserved.map((r, j) =>
                        j === i ? { ...r, label: e.target.value } : r,
                      );
                      set({ reserved });
                    }}
                  />
                  <Button
                    variant="ghost"
                    aria-label={p.removeReserved(i)}
                    title={p.removeReserved(i)}
                    onClick={() => set({ reserved: value.reserved.filter((_, j) => j !== i) })}
                  >
                    <X aria-hidden className="size-4" />
                  </Button>
                </div>
                <FieldMessages id={id(`reserved-${i}-error`)} messages={errors} />
              </li>
            );
          })}
        </ul>
        <Button
          className="mt-2"
          icon={<Plus aria-hidden className="size-4" />}
          onClick={() => set({ reserved: [...value.reserved, { cidr: '', label: '' }] })}
        >
          {p.addReserved}
        </Button>
      </fieldset>

      <div className="grid gap-x-3 sm:grid-cols-3">
        <Field
          label={p.regionPrefix}
          htmlFor={id('region')}
          help={p.regionPrefixHelp}
          errors={errorsAt(`${BASE}/regionPrefixLength`)}
        >
          <input
            id={id('region')}
            className="input"
            type="number"
            min={12}
            max={20}
            value={value.regionPrefixLength}
            aria-describedby={`${id('region')}-help`}
            onChange={(e) => set({ regionPrefixLength: e.target.value })}
          />
        </Field>
        <Field
          label={p.hubPrefix}
          htmlFor={id('hub')}
          help={p.hubPrefixHelp}
          errors={errorsAt(`${BASE}/hubPrefixLength`)}
        >
          <input
            id={id('hub')}
            className="input"
            type="number"
            min={22}
            max={26}
            value={value.hubPrefixLength}
            aria-describedby={`${id('hub')}-help`}
            onChange={(e) => set({ hubPrefixLength: e.target.value })}
          />
        </Field>
        <Field label={p.defaultSize} htmlFor={id('size')} errors={errorsAt(`${BASE}/defaultAttachmentSize`)}>
          <select
            id={id('size')}
            className="input"
            value={value.defaultAttachmentSize}
            onChange={(e) => set({ defaultAttachmentSize: e.target.value as AttachmentSize })}
          >
            {SIZE_ORDER.map((size) => (
              <option key={size} value={size}>
                {sizeOptionLabel(size)}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
}
